/**
 * @file
 * Completes configured CSS grids with invisible filler items.
 *
 * Filler items visually complete the final row of a grid.
 *
 * Example:
 *
 * 4 columns + 10 real items
 * → 2 filler items
 *
 * Filler handling is purely progressive enhancement:
 * the actual grid content remains fully visible and usable
 * when JavaScript is unavailable.
 */

(function (Drupal, once) {
  "use strict";

  const FILLER_CLASS = "grid-filler";
  const cleanups = new WeakMap();
  const pending = new Map();
  let frame = null;

  function flushUpdates() {
    frame = null;
    // Read every visible grid before any filler mutation invalidates layout.
    const updates = [...pending].map(([container, classes]) => {
      if (!container.isConnected || container.closest('[hidden]')) return null;
      return { container, classes, columns: getColumnCount(container) };
    });
    pending.clear();
    updates.forEach((update) => {
      if (update) updateFillers(update.container, update.classes, update.columns);
    });
  }

  /**
   * Grids that should receive filler items.
   *
   * `.teaser-grid` covers:
   * - main product catalog
   * - related titles
   * - author product lists
   * - Open Access
   *
   * News and editions remain separate because their filler elements need
   * the additional `.views-row` class.
   */
  const GRID_CONFIGS = [
    {
      selector: ".teaser-grid",
    },
    {
      selector: ".view-news.view-display-id-teasers .view-content",
      fillerClasses: ["views-row"],
    },
    {
      selector: ".view-editions.view-display-id-page_1 .view-content",
      fillerClasses: ["views-row"],
    },
  ];

  /**
   * Get the number of currently resolved CSS Grid columns.
   *
   * @param {HTMLElement} container
   *   The grid container.
   *
   * @return {number}
   *   Number of columns, or 0 when the element is not a grid.
   */
  function getColumnCount(container) {
    const styles = getComputedStyle(container);

    if (
      styles.display !== "grid" &&
      styles.display !== "inline-grid"
    ) {
      return 0;
    }

    const columns = styles.gridTemplateColumns;

    if (!columns || columns === "none") {
      return 0;
    }

    return columns.trim().split(/\s+/).length;
  }

  /**
   * Get all existing direct-child filler items.
   *
   * @param {HTMLElement} container
   *   The grid container.
   *
   * @return {HTMLElement[]}
   *   Existing filler elements.
   */
  function getFillers(container) {
    return Array.from(container.children).filter(
      (child) => child.classList.contains(FILLER_CLASS),
    );
  }

  /**
   * Remove all filler items from a grid.
   *
   * @param {HTMLElement[]} fillers
   *   Filler elements to remove.
   */
  function removeFillers(fillers) {
    fillers.forEach((filler) => filler.remove());
  }

  /**
   * Determine which HTML element should be used for fillers.
   *
   * List containers require <li>; all other grid containers
   * use <div>.
   *
   * @param {HTMLElement} container
   *   The grid container.
   *
   * @return {string}
   *   HTML tag name for the filler.
   */
  function getFillerTag(container) {
    return container.matches("ul, ol") ? "li" : "div";
  }

  /**
   * Create one filler element.
   *
   * @param {HTMLElement} container
   *   The grid container.
   * @param {string[]} additionalClasses
   *   Optional additional CSS classes.
   *
   * @return {HTMLElement}
   *   The new filler element.
   */
  function createFiller(container, additionalClasses = []) {
    const filler = document.createElement(getFillerTag(container));

    filler.classList.add(FILLER_CLASS, ...additionalClasses);
    filler.setAttribute("aria-hidden", "true");

    return filler;
  }

  /**
   * Update filler items for one grid.
   *
   * @param {HTMLElement} container
   *   The grid container.
   * @param {string[]} additionalClasses
   *   Optional additional filler classes.
   */
  function updateFillers(container, additionalClasses, columnCount) {
    if (!container.isConnected) {
      return;
    }

    const fillers = getFillers(container);

    if (!columnCount) {
      removeFillers(fillers);
      return;
    }

    const itemCount = container.children.length - fillers.length;

    const needed =
      itemCount === 0
        ? 0
        : (columnCount - (itemCount % columnCount)) % columnCount;

    /**
     * Nothing changed, so keep the existing fillers.
     */
    if (fillers.length === needed) {
      return;
    }

    removeFillers(fillers);

    if (needed === 0) {
      return;
    }

    const fragment = document.createDocumentFragment();

    for (let index = 0; index < needed; index += 1) {
      fragment.appendChild(
        createFiller(container, additionalClasses),
      );
    }

    container.appendChild(fragment);
  }

  /**
   * Install automatic filler handling on one grid.
   *
   * Filler calculation is repeated when:
   * - the container changes size;
   * - real direct-child items are added or removed.
   *
   * Updates are scheduled through requestAnimationFrame() so
   * multiple changes within one rendering cycle cause only one
   * recalculation.
   *
   * @param {HTMLElement} container
   *   The grid container.
   * @param {string[]} additionalClasses
   *   Optional additional filler classes.
   */
  function install(container, additionalClasses = []) {
    function scheduleUpdate() {
      pending.set(container, additionalClasses);
      if (frame === null) frame = requestAnimationFrame(flushUpdates);
    }

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(container);

    const mutationObserver = new MutationObserver((mutations) => {
      const contentChanged = mutations.some((mutation) =>
        [...mutation.addedNodes, ...mutation.removedNodes].some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            !node.classList?.contains(FILLER_CLASS),
        ),
      );

      if (contentChanged) {
        scheduleUpdate();
      }
    });

    mutationObserver.observe(container, {
      childList: true,
    });

    scheduleUpdate();

    cleanups.set(container, () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      pending.delete(container);
      if (!pending.size && frame !== null) {
        cancelAnimationFrame(frame);
        frame = null;
      }
    });
  }

  /**
   * Drupal behavior.
   */
  Drupal.behaviors.gridFillers = {
    attach(context) {
      GRID_CONFIGS.forEach((config) => {
        once(
          "grid-fillers",
          config.selector,
          context,
        ).forEach((container) => {
          install(container, config.fillerClasses ?? []);
        });
      });
    },
    detach(context, settings, trigger) {
      if (trigger !== "unload") return;
      GRID_CONFIGS.forEach(({ selector }) => {
        const containers = [...context.querySelectorAll(selector)];
        if (context.matches?.(selector)) containers.unshift(context);
        once.remove("grid-fillers", containers).forEach((container) => {
          cleanups.get(container)?.();
          cleanups.delete(container);
        });
      });
    },
  };
})(Drupal, once);
